import { useMemo, useState } from 'react';
import { formatTime } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, TextLink } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { completeTask, sendMessage, useContactMessages, useWhatsAppConnection } from '../api';
import { berlinInstant, formatDayKey, relativeDay, shortDay } from '../format';
import type { CreateMessageBody } from '../types';
import { EditorCard, SplitSendButton } from './EditorCard';
import { EditorField, FollowUpField, validTime } from './fields';
import { followUpOf, isNextStepRequired, isValidation, useNextStepGate, type EditorProps } from './shared';
import styles from './editor.module.css';

const MAX_CHARS = 1024;
const WINDOW_MS = 24 * 60 * 60 * 1000;

interface Errors {
  text?: string | undefined;
  followUp?: string | undefined;
}

/** Kontaktakte II.E3 „WhatsApp": the thread's last inbound line, the 24-hour window, text with counter, follow-up. */
export function WhatsAppEditor({ record, task, onClose, onDone }: EditorProps) {
  const connection = useWhatsAppConnection(true);
  const thread = useContactMessages(record.id, 'whatsapp', true);
  const [text, setText] = useState('');
  const [followUp, setFollowUp] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const lastInbound = useMemo(
    () => (thread.data?.items ?? []).find((item) => item.direction === 'inbound') ?? null,
    [thread.data],
  );
  const windowUntil = lastInbound ? new Date(new Date(lastInbound.createdAt).getTime() + WINDOW_MS) : null;
  const windowOpen = windowUntil !== null && windowUntil.getTime() > Date.now();
  const optIn = record.properties
    .flatMap((group) => group.rows)
    .some((row) => row.key === 'whatsappOptIn' && row.value !== null && !/^nicht/.test(row.value));
  const phone = record.header.phone;
  const subtitle = windowOpen
    ? `Freies Fenster offen bis ${shortDay(windowUntil.toISOString())}, ${formatTime(windowUntil.toISOString())}`
    : optIn
      ? 'Kein offenes 24-Stunden-Fenster · WhatsApp-Opt-in liegt vor'
      : 'Kein offenes 24-Stunden-Fenster';
  const history = lastInbound
    ? `Letzte Nachricht des Kontakts ${relativeDay(lastInbound.createdAt).toLowerCase()} ${formatTime(lastInbound.createdAt)}: „${
        lastInbound.text ?? '1 Anhang'
      }“`
    : 'Noch keine Nachricht des Kontakts über WhatsApp.';
  const state = connection.data?.state;
  const notConnected = state === 'none' || state === 'qr' || state === 'expired' || state === 'pairing';
  const offline = state === 'disconnected';
  const over = text.length > MAX_CHARS;
  const taskTitle = `Follow-up zu: ${text.trim().split('\n')[0] || `WhatsApp ${record.header.name}`}`;

  function buildBody(sendAt: string | undefined): CreateMessageBody | null {
    const next: Errors = {};
    if (text.trim() === '') next.text = 'Bitte einen Text angeben.';
    if (over) next.text = 'Höchstens 1024 Zeichen.';
    if (followUp && !validTime(time)) next.followUp = 'Bitte eine Uhrzeit im Format HH:MM angeben.';
    setErrors(next);
    if (Object.keys(next).length > 0) return null;
    const body: CreateMessageBody = { contactId: record.id, channel: 'whatsapp', text: text.trim() };
    if (sendAt) body.sendAt = sendAt;
    const due = followUpOf(followUp, date, time);
    if (due) body.followUp = due;
    return body;
  }

  const gate = useNextStepGate(record.header.name, async (step) => {
    if (!task) return;
    const done = await completeTask(task.id, { outcomeType: 'whatsapp_sent', nextStep: step });
    onDone({
      toast: 'WhatsApp gesendet.',
      next:
        done.next === 'book_appointment'
          ? { editor: 'appointment', completesTaskId: done.createdTaskId }
          : undefined,
    });
  });

  async function send(sendAt: string | undefined, toast: string) {
    const body = buildBody(sendAt);
    if (!body) return;
    setPending(true);
    setBanner(null);
    try {
      await sendMessage(body);
    } catch (error) {
      if (isValidation(error)) {
        const fields = error.fields;
        const mapped: Errors = {
          text: fields['text'],
          followUp: fields['followUp.dueOn'] ?? fields['followUp.dueTime'],
        };
        setErrors(mapped);
        if (fields['to'] || !(mapped.text || mapped.followUp)) setBanner(fields['to'] ?? error.message);
      } else setBanner(errorMessage(error));
      setPending(false);
      return;
    }
    if (task) {
      try {
        const done = await completeTask(task.id, { outcomeType: 'whatsapp_sent' });
        onDone({
          toast,
          next:
            done.next === 'book_appointment'
              ? { editor: 'appointment', completesTaskId: done.createdTaskId }
              : undefined,
        });
      } catch (error) {
        setPending(false);
        if (isNextStepRequired(error)) gate.open();
        else setBanner(errorMessage(error));
      }
      return;
    }
    onDone({ toast });
  }

  return (
    <>
      <EditorCard
        tone="success"
        dot="var(--success-fg)"
        title={`WhatsApp · ${phone ?? record.header.name}`}
        subtitle={subtitle}
        footnote="Außerhalb des 24-Stunden-Fensters ist nur eine freigegebene Vorlage zulässig."
        label="WhatsApp-Editor"
        onClose={onClose}
        primary={
          <SplitSendButton
            loading={pending}
            disabled={phone === null || notConnected}
            onSend={() => void send(undefined, 'WhatsApp gesendet.')}
            onSendLater={(day, at) =>
              void send(
                berlinInstant(day, at || '09:00'),
                `WhatsApp geplant für ${formatDayKey(day)}, ${at || '09:00'}.`,
              )
            }
          />
        }
      >
        {phone === null ? <Banner tone="warning">Der Kontakt hat keine Telefonnummer.</Banner> : null}
        {notConnected ? (
          <Banner
            tone="warning"
            title="WhatsApp ist nicht verbunden."
            actions={
              <TextLink to="/settings/whatsapp" size="small">
                WhatsApp verbinden
              </TextLink>
            }
          >
            Nachrichten gehen über Ihre eigene WhatsApp-Session raus.
          </Banner>
        ) : null}
        {offline ? (
          <Banner tone="info">
            Ihre WhatsApp-Session ist gerade offline. Die Nachricht wartet im Postausgang und geht automatisch
            raus, sobald die Session wieder steht.
          </Banner>
        ) : null}
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <EditorField label="Verlauf">
          <p className={styles.text}>{history}</p>
        </EditorField>
        <EditorField label="Nachricht" hint={`${text.length} von ${MAX_CHARS} Zeichen`} error={errors.text}>
          <textarea
            className={clsx(styles.textarea, over && styles.inputInvalid)}
            rows={4}
            aria-label="Nachricht"
            value={text}
            onChange={(event) => setText(event.target.value)}
            autoFocus
          />
        </EditorField>
        <FollowUpField
          checked={followUp}
          onCheckedChange={setFollowUp}
          date={date}
          time={time}
          onDateChange={setDate}
          onTimeChange={setTime}
          taskTitle={taskTitle}
          error={errors.followUp}
        />
      </EditorCard>
      {gate.dialog}
    </>
  );
}
